import { useState } from 'react'
import { BellRing, Type } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import StatusBadge from '../../components/StatusBadge'
import { FormCols, InfoNote } from '../../components/Form'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { saveAwarenessScreenSettings } from './engagementService'
import { awarenessStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

const T = S.settings

/**
 * إعدادات شاشة التوعية — `save-settings`.
 *
 * ONE endpoint for all three, unlike the policies panel next door where each setting is its
 * own call: `SaveScreenSettings` takes whether the screen is offered at all and what it is
 * called in each language, and writes them together. So there is one Save, and the switch
 * does NOT write on flip — it would post the two names alongside it, half-typed.
 *
 * The notification line is READ-ONLY. The payload says whether a template is active;
 * editing one belongs to the notifications screen, not this one.
 */
export default function AwarenessSettingsPanel({ settings, onChange }) {
  const toast = useToast()
  const [nameAr, setNameAr] = useState(settings.screenNameAr)
  const [nameEn, setNameEn] = useState(settings.screenNameEn)
  const [saving, setSaving] = useState(false)

  const dirty = nameAr !== settings.screenNameAr || nameEn !== settings.screenNameEn

  const save = async () => {
    setSaving(true)
    /* `enabled` still goes on the wire — one call writes all three — but it is
       READ from the page rather than edited here: the switch moved to the page
       header, where every other activation switch in the panel lives. Sending
       the current value is what stops a name save from turning the screen off. */
    const outcome = await saveAwarenessScreenSettings({
      enabled: settings.enabled,
      screenNameAr: nameAr,
      screenNameEn: nameEn,
    })
    setSaving(false)

    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || T.saved)
    onChange({ screenNameAr: nameAr, screenNameEn: nameEn })
  }

  return (
    <>
      <Card
        title={T.screen}
        subtitle={T.screenHint}
        footer={
          /* One Save for all three, because one call writes all three. Disabled until
             something has actually changed, so the button says whether it is needed. */
          <Button onClick={save} disabled={saving || !dirty}>
            {saving ? T.saving : T.save}
          </Button>
        }
      >
        <FormCols>
          <FormRow label={T.nameAr} hint={T.nameHint}>
            {(id) => <TextField id={id} icon={Type} value={nameAr} onChange={setNameAr} />}
          </FormRow>
          <FormRow label={T.nameEn}>
            {(id) => (
              <TextField id={id} dir="ltr" icon={Type} value={nameEn} onChange={setNameEn} />
            )}
          </FormRow>
        </FormCols>

        {/* The screen being off hides it from every learner at once. The switch
            itself is in the page header now, so this reads as the consequence of
            a state rather than a warning attached to a control. */}
        {!settings.enabled && (
          <InfoNote>
            <bdi>{T.disabledNote}</bdi>
          </InfoNote>
        )}
      </Card>

      <Card title={T.notification}>
        <InfoNote icon={BellRing}>
          <bdi>{T.notificationHint}</bdi>{' '}
          <StatusBadge tone={settings.notify ? 'success' : 'dark'}>
            {settings.notify ? T.notifyOn : T.notifyOff}
          </StatusBadge>
        </InfoNote>
      </Card>
    </>
  )
}
